import React, { useEffect } from 'react';
import HelpLayout from '../../../components/help/HelpLayout';

const ApplicationsHelp = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { name: 'User Guide', href: '/help/getting-started' },
    { name: 'Course Applications' }
  ];

  return (
    <HelpLayout title="Course Applications" breadcrumbs={breadcrumbs}>
      <div className="help-content">
        <div className="card">
          <div className="card-body">
            <h2 style={{
              fontSize: '24px',
              fontWeight: 'bold',
              color: 'var(--primary-black)',
              marginBottom: '16px'
            }}>
              Course Application Process
            </h2>
            <p style={{
              fontSize: '16px',
              color: 'var(--text-gray)',
              lineHeight: 1.6
            }}>
              Step-by-step guide to applying for training programs, including requirements, documents, and status tracking.
            </p>

            {/* Prerequisites Section */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              marginTop: '24px',
              border: '1px solid #f59e0b'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#92400e',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                ⚠️ Before You Apply
              </h3>
              <p style={{ color: '#92400e', margin: '0 0 12px 0', lineHeight: 1.6 }}>
                <strong>Important:</strong> You must have a complete profile before applying for any course.
                Incomplete profiles will prevent successful application submission.
              </p>
              <p style={{ color: '#92400e', margin: 0, lineHeight: 1.6 }}>
                Visit your <strong>Profile</strong> page to ensure all required information is filled in,
                including personal details, education background, and location information.
              </p>
            </div>

            {/* Application Process Steps */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📝 Step-by-Step Application Process
              </h3>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                {/* Step 1 */}
                <div style={{
                  padding: '20px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--primary-yellow)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--primary-black)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      backgroundColor: 'var(--primary-yellow)',
                      color: 'var(--primary-black)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>1</span>
                    Find Your Course
                  </h4>
                  <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, margin: 0 }}>
                    Browse the <strong>Courses</strong> page, use search and filters to find training programs
                    that match your interests and qualifications. Read course descriptions and requirements carefully.
                  </p>
                </div>

                {/* Step 2 */}
                <div style={{
                  padding: '20px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--primary-yellow)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--primary-black)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      backgroundColor: 'var(--primary-yellow)',
                      color: 'var(--primary-black)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>2</span>
                    Check Requirements
                  </h4>
                  <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, margin: 0 }}>
                    Verify that you meet the entry requirements including minimum education level,
                    age requirements, and any specific prerequisites for the course.
                  </p>
                </div>

                {/* Step 3 */}
                <div style={{
                  padding: '20px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--primary-yellow)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--primary-black)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      backgroundColor: 'var(--primary-yellow)',
                      color: 'var(--primary-black)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>3</span>
                    Complete Your Profile
                  </h4>
                  <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, margin: 0 }}>
                    Ensure your profile is 100% complete with all personal information, education background,
                    and location details. This is mandatory for course applications.
                  </p>
                </div>

                {/* Step 4 */}
                <div style={{
                  padding: '20px',
                  backgroundColor: 'var(--background-gray)',
                  borderRadius: '8px',
                  borderLeft: '4px solid var(--primary-yellow)'
                }}>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: 'var(--primary-black)',
                    marginBottom: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <span style={{
                      backgroundColor: 'var(--primary-yellow)',
                      color: 'var(--primary-black)',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold'
                    }}>4</span>
                    Submit Application
                  </h4>
                  <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, margin: 0 }}>
                    Click "Apply Now" on the course page, fill in your motivation letter and any additional notes,
                    acknowledge the requirements, and submit your application.
                  </p>
                </div>
              </div>
            {/* Required Documents Section */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📄 Required Documents and Information
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Essential Documents
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  While documents are not uploaded during the online application, you should have these ready
                  for verification if your application is accepted:
                </p>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>South African ID Document:</strong> Valid identity document (required)</li>
                  <li><strong>Matric Certificate:</strong> Grade 12 certificate or equivalent qualification</li>
                  <li><strong>Highest Qualification:</strong> Certificates for any post-school education</li>
                  <li><strong>Proof of Address:</strong> Recent utility bill or bank statement</li>
                  <li><strong>CV/Resume:</strong> Current curriculum vitae (if applicable)</li>
                  <li><strong>Medical Certificate:</strong> For certain courses requiring physical fitness</li>
                </ul>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Application Form Information
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '12px' }}>
                  When applying online, you'll need to provide:
                </p>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                  gap: '16px',
                  marginBottom: '16px'
                }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      💭 Motivation Letter
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Explain why you want to take this course and how it fits your career goals
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      📝 Additional Notes
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Any additional information or special circumstances to consider
                    </p>
                  </div>
                  <div style={{
                    padding: '16px',
                    backgroundColor: 'var(--background-gray)',
                    borderRadius: '6px'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: 'var(--primary-black)',
                      marginBottom: '8px'
                    }}>
                      ✅ Acknowledgments
                    </h5>
                    <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0 }}>
                      Confirm you understand requirements and commit to the program
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Application Status Tracking */}
            <div style={{ marginTop: '32px' }}>
              <h3 style={{
                fontSize: '20px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px'
              }}>
                📊 Application Status Tracking
              </h3>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Application Lifecycle
                </h4>
                <p style={{ color: 'var(--text-gray)', lineHeight: 1.6, marginBottom: '16px' }}>
                  Track your application progress through these stages:
                </p>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    borderLeft: '4px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '4px'
                    }}>
                      📝 Applied
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Application submitted and received by GCRA
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#fef3c7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #f59e0b'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#d97706',
                      marginBottom: '4px'
                    }}>
                      🔍 Under Review
                    </h5>
                    <p style={{ fontSize: '14px', color: '#d97706', margin: 0 }}>
                      GCRA staff reviewing your application and qualifications
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '4px'
                    }}>
                      ✅ Accepted
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Application approved - prepare for registration
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#e0f2fe',
                    borderRadius: '6px',
                    borderLeft: '4px solid #0284c7'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#0284c7',
                      marginBottom: '4px'
                    }}>
                      📋 To Register
                    </h5>
                    <p style={{ fontSize: '14px', color: '#0284c7', margin: 0 }}>
                      Complete registration process and submit documents
                    </p>
                  </div>

                  <div style={{
                    padding: '16px',
                    backgroundColor: '#dcfce7',
                    borderRadius: '6px',
                    borderLeft: '4px solid #16a34a'
                  }}>
                    <h5 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#16a34a',
                      marginBottom: '4px'
                    }}>
                      🎓 Registered
                    </h5>
                    <p style={{ fontSize: '14px', color: '#16a34a', margin: 0 }}>
                      Officially enrolled - await course start date
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '24px' }}>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'var(--primary-black)',
                  marginBottom: '12px'
                }}>
                  Where to Check Status
                </h4>
                <ul style={{
                  color: 'var(--text-gray)',
                  lineHeight: 1.6,
                  paddingLeft: '20px',
                  marginBottom: '16px'
                }}>
                  <li><strong>Dashboard:</strong> View all your applications in the "Course Registrations" section</li>
                  <li><strong>Email Notifications:</strong> Receive updates when status changes</li>
                  <li><strong>Announcements:</strong> Check for important updates and deadlines</li>
                  <li><strong>Course Pages:</strong> See your application status on individual course cards</li>
                </ul>
              </div>
            </div>

            {/* Tips and Best Practices */}
            <div style={{
              padding: '20px',
              backgroundColor: 'var(--background-gray)',
              borderRadius: '8px',
              marginTop: '32px'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: 'var(--primary-black)',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                💡 Application Tips and Best Practices
              </h3>
              <ul style={{
                color: 'var(--text-gray)',
                lineHeight: 1.6,
                paddingLeft: '20px',
                margin: 0
              }}>
                <li><strong>Apply early:</strong> Popular courses fill up quickly, don't wait until the last minute</li>
                <li><strong>Write a compelling motivation letter:</strong> Explain your goals and commitment clearly</li>
                <li><strong>Double-check requirements:</strong> Ensure you meet all entry requirements before applying</li>
                <li><strong>Complete your profile:</strong> Incomplete profiles will prevent application submission</li>
                <li><strong>Keep documents ready:</strong> Have all required documents prepared for verification</li>
                <li><strong>Monitor your email:</strong> Important updates are sent via email notifications</li>
                <li><strong>Check your dashboard regularly:</strong> Stay updated on application status changes</li>
                <li><strong>Contact support if needed:</strong> Reach out if you have questions about the process</li>
                <li><strong>Be honest and accurate:</strong> Provide truthful information in all application fields</li>
                <li><strong>Follow up appropriately:</strong> Check status regularly but avoid excessive inquiries</li>
              </ul>
            </div>

            {/* Withdrawal Information */}
            <div style={{
              padding: '20px',
              backgroundColor: '#fee2e2',
              borderRadius: '8px',
              marginTop: '24px',
              border: '1px solid #ef4444'
            }}>
              <h3 style={{
                fontSize: '18px',
                fontWeight: 'bold',
                color: '#dc2626',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                🚫 Application Withdrawal
              </h3>
              <p style={{ color: '#dc2626', margin: '0 0 12px 0', lineHeight: 1.6 }}>
                If you need to withdraw your application, you can do so from your Dashboard while the
                application is in "Applied", "Under Review", "Accepted", or "To Register" status.
              </p>
              <p style={{ color: '#dc2626', margin: 0, lineHeight: 1.6 }}>
                <strong>Note:</strong> Once you're registered and training has begun, withdrawal may have
                different implications. Contact GCRA support for guidance in such cases.
              </p>
            </div>

            </div>
          </div>
        </div>
      </div>
    </HelpLayout>
  );
};

export default ApplicationsHelp;
